import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { FormattedMessage } from 'react-intl'
import { uniq, flatten, compact, isEmpty, union } from 'lodash'

// components
import TreeList from 'components/Panel/TreeList'
import TreeListConcepts from 'components/Panel/TreeListConcepts'
import { Title1, Title4 } from 'components/Titles'
import { ParagraphLegend } from 'components/Paragraphs'

import Abacus from 'components/Panel/Abacus'

// icons
import ListArrowRight from 'common/icons/list-arrow-right'

// hooks
import useUserData, { useAllUserData } from 'flow/userData/useUserData'
import { nodeIdToIndex } from 'flow/queryparams'
import { useSelection } from 'flow/interactions/accessors'
import { useMedia } from 'hooks/custom/useMedia'

// data
import concepts from 'data/output/concept-nodes.json'
import edges from 'data/output/concept-deduped-links.json'
import conceptSourceToLinkIndex from 'data/output/concept-source-to-links-index.json'
import conceptTargetToLinkIndex from 'data/output/concept-target-to-links-index.json'

const panelWidth = 540


const Container = styled.div`
  position: relative;
  min-width: ${({ isColDisplay }) => isColDisplay ? panelWidth : 0}px;
  max-width: ${({ isColDisplay }) => isColDisplay ? panelWidth : 0}px;
  background-color: ${({ theme, isConcept }) => !isConcept ? theme.blueUltraDark : theme.purple};
  height: 100%;
`

const PanelContainer = styled.div`
  height: 100%;
  padding: 60px 80px;
  overflow-x: hidden;
  overflow-y: auto;
`
const MainTitle = styled(Title1)`
  margin-bottom: 30px;
`

const Nature = styled(Title4)`
  color: ${({ theme }) => theme.tonic};
  margin-bottom: 15px;
`

const Credits = styled.a`
  text-align: right;
  display: block;
  margin-top: 30px;
  color: ${({ theme }) => theme.white};
  text-decoration: none;
  :hover {
    text-decoration: underline;
  }
`

const Button = styled.span`
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 40px;
  background-color: ${({ theme }) => theme.blueDark};
  left: ${({ isColDisplay }) => isColDisplay ? -20 : -50}px;
  transform: rotate(${({ isColDisplay }) => isColDisplay ? 0 : -180}deg);
  position: absolute;
  top: 60px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ theme }) => theme.blueUltraDark};
  }
`


const getRelatedConcepts = (conceptNode) => {
  const sourcesIndexes = conceptSourceToLinkIndex[conceptNode.data.id]
  const targetIndexes = conceptTargetToLinkIndex[conceptNode.data.id]
  const indexes = union(sourcesIndexes, targetIndexes)

  const ids = uniq([conceptNode.data.id, ...flatten(indexes.map(index => [edges[index].source, edges[index].target]))])

  const nodes = ids.map(id => ({
    data: {
      ...concepts[nodeIdToIndex[id]]
    }
  }))

  return nodes
}

const Panel = ({ currentNode }) => {
  const [selection, setSelection] = useSelection()
  const scrollRef = useRef()
  const nature = currentNode.data.nature
  const isHome = nature === 'goal'
  const isConcept = nature === 'concept'
  const [isColDisplay, setIsColDisplay] = useState(true)
  const [userData] = useUserData()
  const [userDataAll] = useAllUserData()
  const isMobile = useMedia("(max-width: 768px)")

  const nodesAll = useMemo(() => {
    let allChildren
    if (isConcept) {
      allChildren = getRelatedConcepts(currentNode)
    } else {
      const children = compact(currentNode.children)
      const grandChildren = children && compact(flatten(currentNode.children.map(d => d.children)))
      allChildren = !isEmpty(grandChildren) ? grandChildren : children
    }

    return allChildren
  }, [currentNode, isConcept])

  const nodes = useMemo(() => {
    if (!userData || !userDataAll) return null
    return nodesAll.map((node) => {
      return {
        ...node,
        userData: userData[node.data.id],
        userDataAll: userDataAll[node.data.id]
      }
    })
  }, [nodesAll, userData, userDataAll])

  const togglPanel = useCallback(() => {
    setIsColDisplay((state) => !state)
  }, [])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0
    }
  }, [currentNode])
  

  return (
    <Container
      isConcept={isConcept}
      isColDisplay={!isMobile && isColDisplay}
      onClick={() => setSelection([])}
    >
      {!isMobile && <Button isColDisplay={isColDisplay} onClick={togglPanel}>
        <ListArrowRight />
      </Button>}
      {
        isColDisplay && (
          <PanelContainer ref={scrollRef}>
            {
              !isHome && (
                <Nature>
                  {nature}
                </Nature>
              )
            }
           <MainTitle alt={isHome ? currentNode.data.id : ''}>
              {
                isHome
                  ? <FormattedMessage id='col-home-title' />
                  : currentNode.data.label
              }
            </MainTitle>
            {
              nodes && (
                <Abacus
                  nodes={nodes}
                  isConcept={isConcept}
                  currentNode={currentNode}
                  selection={selection}
                  setSelection={setSelection}
                />
              )
            }
            {
              isConcept
                ? (
                  <TreeListConcepts
                    currentNode={currentNode}
                    nodes={nodes}
                  />
                )
                : (
                  <TreeList
                    currentNode={currentNode}
                  />
                )

            }
            <Credits href='/CREDITS.md' target='_blank'>
              <ParagraphLegend>
                {/*<FormattedMessage id='tech-credits' />*/}
              </ParagraphLegend>
            </Credits>
          </PanelContainer>
        )
      }
    </Container>
  )
}

export default Panel
